/** Client for the Mapping Studio API (task 4080.8). */
import { apiFetch } from "../../app/api";

const BASE = "/api/v1/mapping/proposals";

export interface ProposalRow {
  id: string;
  outcome: string;
  concept_key: string | null;
  target_object: string | null;
  target_attribute: string | null;
  certified_mapping_exists: boolean;
  decision: string | null;
  recorded_at: string;
}

export interface TargetCandidate {
  objectname: string;
  attributename: string;
  title: string | null;
  maxtype: string | null;
  length: number | null;
  required: boolean | null;
  persistent: boolean | null;
  domainid: string | null;
  scope: string | null;
  destination: string;
  unverified_reason: string | null;
  profile_reference: { profile_version: string; section: string; row_key: string } | null;
  pack_reference: { pack_key: string; pack_version: string } | null;
}

export interface ProposedTarget {
  target_object: string;
  target_attribute: string;
  transformation: string | null;
  reasoning: string;
  known_limits: string[];
}

export interface ProposalView {
  proposal: {
    outcome: string;
    concept: { key: string; name: string; version: number } | null;
    candidate_targets: TargetCandidate[];
    proposed: ProposedTarget | null;
    alternatives: ProposedTarget[];
    no_alternative_statement: string | null;
    rejected_targets: { target_object: string; target_attribute: string; reason: string }[];
    existing_mappings: {
      rule_key: string;
      target: string;
      support_level: string;
      applies: boolean;
    }[];
    missing_context: string[];
    reliability_references: string[];
    explanation: string | null;
    repair_attempts: number;
  };
  observation: {
    label: string | null;
    transcribed_text: string | null;
    unit_text: string | null;
    region_key: string;
  };
  decision: { decision: string; reason: string; decided_by: string } | null;
  mapping: { id: string; version: number; lifecycle_state: string; target: string } | null;
  support: { level: string; reason: string } | null;
}

export interface MappingDecision {
  rule_key: string;
  scope_level: "engagement" | "customer";
  context: Record<string, string>;
  owner_party: string;
  authority: string;
  proof_limits: string[];
  test_cases: {
    case_kind: "clean" | "edge" | "negative";
    description: string;
    input: unknown;
    expected: unknown;
  }[];
  revalidation_triggers: string[];
  rollback_path: string;
  reason: string;
}

export interface DecisionResult {
  awaiting_second_approver: boolean;
}

export function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

function send<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export function listProposals(engagementId: string, undecidedOnly: boolean): Promise<ProposalRow[]> {
  const query = new URLSearchParams({
    engagement_id: engagementId,
    undecided_only: String(undecidedOnly),
  });
  return apiFetch<ProposalRow[]>(`${BASE}?${query.toString()}`);
}

export function getProposal(id: string): Promise<ProposalView> {
  return apiFetch<ProposalView>(`${BASE}/${id}`);
}

export function approveProposal(id: string, body: MappingDecision): Promise<DecisionResult> {
  return send(`${BASE}/${id}/approve`, body);
}

export function editProposal(
  id: string,
  target: { target_object: string; target_attribute: string },
  mapping: MappingDecision,
): Promise<DecisionResult> {
  return send(`${BASE}/${id}/edit`, { ...target, mapping });
}

export function rejectProposal(id: string, reason: string): Promise<DecisionResult> {
  return send(`${BASE}/${id}/reject`, { reason });
}
